import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useContent } from "@/context/SiteContentContext";

const IMG_DEFAULT = "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1200&q=85";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [busy, setBusy] = useState(false);

  const heroImg = useContent("signin.hero.image", IMG_DEFAULT);
  const heroQuote = useContent("signin.hero.quote", '"Bonjour, encore — we saved your chair by the window."');
  const overline = useContent("signin.overline", "Welcome back");
  const title = useContent("signin.title", "Sign in to the salon.");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Trim iOS autofill artefacts (stray spaces, keyboard-inserted whitespace).
      // Email is always lowercased server-side; we do it here too so the value
      // never rejects on the first attempt because of an autocapitalised
      // first letter or a trailing space from Safari's suggestion bar.
      await login(email.trim().toLowerCase(), password.trim(), rememberMe);
      toast.success("Welcome back. The door is open.", {
        icon: <img src="/brand/le-salon-door.png" alt="" style={{ width: 36, height: 36 }} />,
        duration: 3000,
      });
      navigate("/feed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" style={{ minHeight: "100vh", display: "grid", gridTemplateColumns: "1fr 1fr" }} className="auth-grid">
      {/* Back arrow — top-left corner, works on both columns */}
      <Link
        to="/"
        data-testid="login-back"
        aria-label="Back to home"
        style={{
          position: "fixed", top: 20, left: 20, zIndex: 10,
          background: "rgba(250, 249, 245, 0.9)", backdropFilter: "blur(8px)",
          border: "1px solid var(--border-default)",
          width: 40, height: 40, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--text-primary)", textDecoration: "none",
          transition: "background 0.2s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-alt)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(250, 249, 245, 0.9)"; }}
      >
        <ArrowLeft size={18} strokeWidth={1.6} />
      </Link>
      <div className="hide-mobile" style={{ position: "relative", overflow: "hidden" }}>
        <img src={heroImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 60%, rgba(28,25,23,0.6) 100%)" }} />
        <div style={{ position: "absolute", left: 48, bottom: 48, color: "var(--text-inverse)", maxWidth: 380 }}>
          <div className="overline" style={{ color: "rgba(250,249,245,0.8)" }}>The Salon</div>
          <p className="font-serif" style={{ fontStyle: "italic", fontSize: 28, lineHeight: 1.3, marginTop: 12 }}>
            {heroQuote}
          </p>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", padding: "48px 56px" }}>
        <Link to="/" data-testid="login-logo" style={{ marginBottom: 32, display: "inline-flex", alignItems: "center", gap: 14 }}>
          <img src="/brand/le-salon-door.png" alt="" aria-hidden="true" style={{ height: 56, width: 56, display: "block" }} />
          <img src="/brand/le-salon-wordmark.png" alt="Le Salon" style={{ height: 46, display: "block" }} />
        </Link>
        <div style={{ maxWidth: 420 }}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>{overline}</div>
          <h1 className="editorial-h1" style={{ fontSize: 52, marginTop: 12, marginBottom: 40 }}>
            {title}
          </h1>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            <div>
              <label className="overline" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="email"
                inputMode="email"
                className="input-belle"
                placeholder="you@belleinspiration.com"
              />
            </div>
            <div>
              <label className="overline" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="current-password"
                className="input-belle"
                placeholder="••••••••"
              />
            </div>
            <label
              data-testid="login-remember-me-row"
              style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, color: "var(--text-secondary)", marginTop: -12 }}
            >
              <input
                type="checkbox"
                data-testid="login-remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: "var(--brand-accent)", width: 16, height: 16 }}
              />
              <span>Keep me signed in for 60 days (on this device).</span>
            </label>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="login-submit-button" style={{ marginTop: 12 }}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p style={{ marginTop: 20, fontSize: 13, color: "var(--text-muted)" }}>
            <Link to="/forgot-password" data-testid="forgot-password-link" className="link-underline" style={{ color: "var(--text-muted)" }}>Forgotten your password?</Link>
          </p>

          <p style={{ marginTop: 20, fontSize: 14, color: "var(--text-secondary)" }}>
            Not yet a member?{" "}
            <Link to="/register" data-testid="login-register-link" className="link-underline" style={{ color: "var(--brand-accent)" }}>Reserve your seat →</Link>
          </p>

          <div style={{ marginTop: 40, padding: 20, background: "var(--bg-alt)", border: "1px solid var(--border-default)" }}>
            <div className="overline">Try the demo</div>
            <p style={{ fontSize: 13, marginTop: 8, marginBottom: 0, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              Use <strong>amelie@demo.bellemaison.com</strong> / <strong>bellemaison2026</strong> — or any of the seeded accounts.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .auth-grid { grid-template-columns: 1fr !important; }
          .hide-mobile { display: none; }
        }
      `}</style>
    </div>
  );
};

export default Login;
